import { isEmpty } from 'lodash/core'
import { Pick } from '@/protocol'
import JSZip from 'jszip'

const eventFunctions = {
  methods: {
    getEventRepeatPatterns() {
      return [
        { text: '毎日', value: Pick.Content.Schedule.Pattern.PATTERN_DAILY },
        {
          text: '平日のみ',
          value: Pick.Content.Schedule.Pattern.PATTERN_WEEKDAY
        },
        {
          text: '休日のみ',
          value: Pick.Content.Schedule.Pattern.PATTERN_WEEKEND
        }
      ]
    },
    getEventColors() {
      return ['#9C27b0', '#00CAE3', '#4CAF50', '#ff9800', '#E91E63', '#FF5252']
    }
  }
}

const conditionFunctions = {
  methods: {
    getConditionTypes() {
      return [
        { text: '性別', value: Pick.Content.Condition.Sample.SAMPLE_GENDER },
        { text: '年齢', value: Pick.Content.Condition.Sample.SAMPLE_AGE },
        {
          text: '滞在時間',
          value: Pick.Content.Condition.Sample.SAMPLE_ELAPSED
        }
      ]
    },
    getConditionComparators() {
      return [
        { text: '=', value: Pick.Content.Condition.Comparator.COMPARATOR_EQ },
        { text: '!=', value: Pick.Content.Condition.Comparator.COMPARATOR_NE },
        { text: '>', value: Pick.Content.Condition.Comparator.COMPARATOR_GT },
        { text: '<', value: Pick.Content.Condition.Comparator.COMPARATOR_LT },
        { text: '<=', value: Pick.Content.Condition.Comparator.COMPARATOR_LE },
        { text: '>=', value: Pick.Content.Condition.Comparator.COMPARATOR_GE }
      ]
    },
    getConditionGenders() {
      return [
        { text: '女性', value: Pick.Detection.Snapshot.Gender.GENDER_FEMALE },
        { text: '男性', value: Pick.Detection.Snapshot.Gender.GENDER_MALE }
      ]
    },
    getConditionAgeGroups() {
      return [
        { text: '10代', value: 10 },
        { text: '20代', value: 20 },
        { text: '30代', value: 30 },
        { text: '40代', value: 40 },
        { text: '50代', value: 50 },
        { text: '60代', value: 60 },
        { text: '70代', value: 70 }
      ]
    },
    getConditionElapsedTimes() {
      return [
        { text: '1秒', value: 1 },
        { text: '2秒', value: 2 },
        { text: '3秒', value: 3 },
        { text: '4秒', value: 4 },
        { text: '5秒', value: 5 },
        { text: '6秒', value: 6 },
        { text: '7秒', value: 7 },
        { text: '8秒', value: 8 },
        { text: '9秒', value: 9 },
        { text: '10秒', value: 10 }
      ]
    },
    getConditionValues(trigger) {
      if (trigger.sample === Pick.Content.Condition.Sample.SAMPLE_AGE) {
        return this.getConditionAgeGroups()
      } else if (
        trigger.sample === Pick.Content.Condition.Sample.SAMPLE_GENDER
      ) {
        return this.getConditionGenders()
      } else if (
        trigger.sample === Pick.Content.Condition.Sample.SAMPLE_ELAPSED
      ) {
        return this.getConditionElapsedTimes()
      }
    },
    getConditionValueString(condition) {
      switch (condition.sample) {
        case Pick.Content.Condition.Sample.SAMPLE_AGE:
          return this.getConditionAgeGroups().find(
            c => c.value === condition.age
          ).text
        case Pick.Content.Condition.Sample.SAMPLE_GENDER:
          return this.getConditionGenders().find(
            c => c.value === condition.gender
          ).text
        case Pick.Content.Condition.Sample.SAMPLE_ELAPSED:
          return this.getConditionElapsedTimes().find(
            c => c.value === condition.elapsed.seconds
          ).text
      }
    }
  }
}

const reportFunctions = {
  methods: {
    classifyEventsByAge(events, gender) {
      const gendered = events.filter(e => e.gender === gender)

      // group by ages
      const tweens = gendered.filter(e => e.age < 10).length
      const teens = gendered.filter(e => 10 <= e.age && e.age < 20).length
      const twenties = gendered.filter(e => 20 <= e.age && e.age < 30).length
      const thirties = gendered.filter(e => 30 <= e.age && e.age < 40).length
      const forties = gendered.filter(e => 40 <= e.age && e.age < 50).length
      const fifties = gendered.filter(e => 50 <= e.age && e.age < 60).length
      const sixties = gendered.filter(e => 60 <= e.age && e.age < 70).length
      const seventies = gendered.filter(e => 70 <= e.age && e.age < 80).length
      const eighties = gendered.filter(e => e.age > 80).length

      return [
        tweens,
        teens,
        twenties,
        thirties,
        forties,
        fifties,
        sixties,
        seventies,
        eighties
      ]
    },
    classifyEvents(events) {
      // all events are pass events
      const passEvents = events.filter(e => e.duration > 0)

      const lookEvents = passEvents
        .filter(e => e.flow !== undefined)
        .filter(e => e.flow.length !== 0)

      const interestEvents = lookEvents.filter(e => e.duration > 5000)

      // we already checked flow presence and length, should be safe to divide
      const focusEvents = lookEvents.filter(e => {
        const msecPerPoint = e.duration / e.flow.length
        let consec = 0,
          max = 0
        e.flow.forEach(f => {
          !isEmpty(f) ? consec++ : (consec = 0)
          if (consec > max) max = consec
        })
        // todo: we're guesstimating the max time correct face detection was present, this should be improved
        return max * msecPerPoint > 2000
      })
      const touchEvents = []

      return {
        pass: passEvents,
        look: lookEvents,
        interest: interestEvents,
        focus: focusEvents,
        touch: touchEvents
      }
    },
    getEventCategories() {
      return [
        { text: '通過', value: 'pass', color: '#188FFB' },
        { text: '視認', value: 'look', color: '#30E396' },
        { text: '興味', value: 'interest', color: '#FDB018' },
        { text: '注視', value: 'focus', color: '#FB4560' },
        { text: 'タッチ', value: 'touch', color: '#775DD0' }
      ]
    },
    getAgeLists() {
      return [
        { min: 10, max: 19 },
        { min: 20, max: 29 },
        { min: 30, max: 39 },
        { min: 40, max: 49 },
        { min: 50, max: 59 },
        { min: 60, max: 69 },
        { min: 70, max: 79 },
        { min: 80, max: 89 },
        { min: 90, max: 99 }
      ]
    }
  }
}

const deviceFunctions = {
  methods: {
    getDeviceType() {
      return [
        {
          type: 'device',
          icon: 'mdi-camera',
          text: 'デバイス'
        },
        {
          type: 'unit',
          icon: 'mdi-orbit',
          text: 'ユニット'
        },
        {
          type: 'group',
          icon: 'mdi-folder',
          text: 'グループ'
        }
      ]
    },
    getDeviceDisplayState() {
      return [
        {
          text: '未詳',
          value: Pick.Device.State.Display.DISPLAY_UNSPECIFIED,
          color: 'gray'
        },
        {
          text: '稼働中',
          value: Pick.Device.State.Display.DISPLAY_POWERED,
          color: 'success'
        },
        {
          text: '電源なし',
          value: Pick.Device.State.Display.DISPLAY_UNPOWERED,
          color: 'warning'
        },
        {
          text: 'エラー',
          value: Pick.Device.State.Display.DISPLAY_ERROR,
          color: 'error'
        }
      ]
    },
    getDeviceContentState() {
      return [
        {
          text: '未詳',
          value: Pick.Device.State.Media.MEDIA_UNSPECIFIED,
          color: 'gray'
        },
        {
          text: 'アイドリング',
          value: Pick.Device.State.Media.MEDIA_IDLE,
          color: 'gray'
        },
        {
          text: '停止',
          value: Pick.Device.State.Media.MEDIA_STOPPED,
          color: 'warning'
        },
        {
          text: '活動中',
          value: Pick.Device.State.Media.MEDIA_PLAYING,
          color: 'success'
        },
        {
          text: '停止',
          value: Pick.Device.State.Media.MEDIA_PAUSED,
          color: 'warning'
        },
        {
          text: 'エラー',
          value: Pick.Device.State.Media.MEDIA_ERROR,
          color: 'error'
        }
      ]
    },
    getDeviceCaptureState() {
      return [
        {
          text: '未詳',
          value: Pick.Device.State.Capture.CAPTURE_UNSPECIFIED,
          color: 'gray'
        },
        {
          text: '稼働中',
          value: Pick.Device.State.Capture.CAPTURE_OPENED,
          color: 'success'
        },
        {
          text: '停止',
          value: Pick.Device.State.Capture.CAPTURE_CLOSED,
          color: 'error'
        },
        {
          text: 'エラー',
          value: Pick.Device.State.Capture.CAPTURE_ERROR,
          color: 'error'
        }
      ]
    },
    getDeviceLogRecordOrigin() {
      return [
        {
          text: '未詳',
          value: Pick.Device.LogRecord.Origin.ORIGIN_UNSPECIFIED
        },
        {
          text: 'データ分析',
          value: Pick.Device.LogRecord.Origin.ORIGIN_NOTTI
        },
        {
          text: 'UI',
          value: Pick.Device.LogRecord.Origin.ORIGIN_QPICK
        },
        {
          text: 'データ交換',
          value: Pick.Device.LogRecord.Origin.ORIGIN_REDIS
        },
        {
          text: 'データ通信',
          value: Pick.Device.LogRecord.Origin.ORIGIN_SIPHON
        }
      ]
    },
    getDeviceLogRecordLevel() {
      return [
        {
          filter: 0,
          text: '未詳',
          value: Pick.Device.LogRecord.Level.LEVEL_UNSPECIFIED,
          icon: 'mdi-progress-question',
          color: 'grey'
        },
        {
          filter: 1,
          text: 'デバッグ',
          value: Pick.Device.LogRecord.Level.LEVEL_DEBUG,
          icon: 'mdi-bug-outline',
          color: 'grey'
        },
        {
          filter: 2,
          text: '情報',
          value: Pick.Device.LogRecord.Level.LEVEL_INFO,
          icon: 'mdi-pound-box-outline',
          color: 'info'
        },
        {
          filter: 3,
          text: '注意',
          value: Pick.Device.LogRecord.Level.LEVEL_WARNING,
          icon: 'mdi-alert-box-outline',
          color: 'warning'
        },
        {
          filter: 4,
          text: 'エラー',
          value: Pick.Device.LogRecord.Level.LEVEL_ERROR,
          icon: 'mdi-alert-circle-outline',
          color: 'error'
        },
        {
          filter: 5,
          text: '重大エラー',
          value: Pick.Device.LogRecord.Level.LEVEL_CRITICAL,
          icon: 'mdi-alert-octagram-outline',
          color: 'error'
        }
      ]
    },
    // device unit group stuff
    getDeviceName(deviceId, devices) {
      return devices.find(device => device.id === deviceId).name
    },
    getUnitName(unitId, units) {
      return units.find(unit => unit.id === unitId).name
    },
    getGroupName(groupId, groups) {
      return groups.find(group => group.id === groupId).name
    },
    // About device type
    getTypeName(type) {
      return this.getDeviceType().find(d => d.type === type).text
    },
    getTypeIcon(type) {
      return this.getDeviceType().find(d => d.type === type).icon
    },
    getDeviceTypeList(devices) {
      return devices.map(d => {
        return {
          id: d.id,
          label: d.name,
          type: 'device'
        }
      })
    },
    getUnitTypeList(units) {
      return units.map(u => {
        return {
          id: u.id,
          label: u.name,
          type: 'unit'
        }
      })
    },
    getUnitTypeFromId(unitIds, units) {
      return unitIds.map(u => {
        return {
          id: u,
          label: this.getUnitName(u, units),
          type: 'unit'
        }
      })
    },
    getDevicesInGroup(group, devices) {
      //extrude group => units => devices
      //get just group => devices
      return devices.filter(d => {
        return (
          d.groups.some(g => g === group.id) &&
          !d.units.some(s => group.units.includes(s))
        )
      })
    },
    getUnitsFromGroupId(groupId, groups) {
      return groups.find(g => g.id === groupId).units
    }
  }
}

const downloadFunctions = {
  methods: {
    /**
     * Download files and create zip with them.
     * @param {string} folderName
     * @param {string[]} urls
     * @param {Number} chunkSize
     * @returns {Blob}
     */
    async zipDownloadedFiles(folderName, urls, chunkSize) {
      const urlChunks = this.splitToChunks(urls, chunkSize)
      const taskChunks = []
      for (const urlChunk of urlChunks) {
        const tasks = []
        for (const url of urlChunk) {
          const task = this.fetchFileContent()
          tasks.push(task(url))
        }
        taskChunks.push(tasks)
      }

      const files = []
      for (const taskChunk of taskChunks) {
        await Promise.all(taskChunk)
          .then(responses => {
            for (const response of responses) {
              files.push(response)
            }
          })
          .catch(errors => {
            errors.forEach(e => console.error(e))
          })
      }

      return this.zipFiles(folderName, files)
    },
    /**
     * Create zip which has given files name and content.
     * @param {string} folderName
     * @param {Array} files each element is object { name: string, content: Blob }
     * @returns {Blob}
     */
    async zipFiles(folderName, files) {
      const zip = new JSZip()
      const folder = zip.folder(folderName)

      for (const file of files) {
        folder.file(file.name, file.content)
      }

      return await zip.generateAsync({ type: 'blob' })
    },
    /**
     * Access to url and get data as Blob.
     * @returns {function(*=): {name: string, content: Blob}} if success return object which is { name: string, content: Blob }
     */
    fetchFileContent() {
      return async url => {
        const urlObj = new URL(url)
        const paths = urlObj.pathname.split('/')
        const name = paths[paths.length - 1]

        const response = await fetch(url)
        const content = await response.blob()

        return { name, content }
      }
    },
    /**
     * Download remote file to local folder.
     * @param {string} filename
     * @param {string} url
     * @returns {Element} anchor tag element object
     */
    downloadFile(filename, url) {
      // Create anchor tag and set up for automatic download
      const anchor = document.createElement('a')
      anchor.type = 'application/octet-stream'
      anchor.download = filename
      anchor.href = url
      document.body.appendChild(anchor)
      // Execute download
      anchor.click()

      return anchor
    },
    /**
     * Split array into a given size of chunks.
     * @param {Array} arr
     * @param {Number} size
     * @returns {Array} the element of array is also Array
     */
    splitToChunks(arr, size) {
      const aryNum = arr.length
      if (size <= 0 || aryNum <= size) {
        return [[...arr]]
      }

      const dev = Math.floor(aryNum / size)
      const mod = aryNum % size
      const chunkNum = mod === 0 ? dev : dev + 1

      let start = 0
      let end = size
      const chunks = []
      for (let i = 0; i < chunkNum; i++) {
        chunks.push(arr.slice(start, end))

        start += size
        if (i === chunkNum && mod === 0) {
          end += mod
        } else {
          end += size
        }
      }

      return chunks
    }
  }
}

export const conditionFunctionsMixin = conditionFunctions
export const eventFunctionsMixin = eventFunctions
export const reportFunctionsMixin = reportFunctions
export const deviceFunctionsMixin = deviceFunctions
export const downloadFunctionsMixin = downloadFunctions
